import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Alert, Box, TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import { Modal } from '~/components/common/Modal'
import type { IntradayBulkWithdrawRefused } from '~/pages/IntradayBidding/hooks/useIntradayBulkWithdraw'
import { intradayBulkWithdrawRefusalCounts } from '~/pages/IntradayBidding/models/intradayBulkWithdrawTargets'
import { formatWithdrawSummary } from '~/pages/IntradayBidding/models/withdrawSummary'

const KEY = 'page:biddingIntraday.bulkWithdraw.refused'

type Props = {
  refused: IntradayBulkWithdrawRefused
  onDismiss: () => void
}

/**
 * 一括板取消で受け付けなかった札があった時のエラーバナー (AC-BIN-112)。 × で閉じる。
 * 見出しは受け付けた件数 (0 件なら見出しを出さず本文だけ)、 本文は理由ごとの件数
 * (GC → 照合 → 状態の順)。 札はバナーに並べず、 「詳細を見る」 で開くダイアログに札の概要
 * (AC-BIN-008) と理由を並べる (`conventions.md`「警告・案内で対象を列挙する時の件数と上限」)。
 *
 * design に無い部品なので、 同じ画面の一括作成の結果のバナー (AC-BIN-006b) と同じく DS `Alert`
 * の error で組み、 導線は UI 辞書 alert-placement §4 の Tertiary sm を右端に置く
 * (Des確認 #12391 で design への反映を依頼)。
 */
export const IntradayBulkWithdrawRefusedBanner = ({
  refused,
  onDismiss,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const counts = intradayBulkWithdrawRefusalCounts(refused.bids)
  return (
    <>
      <Alert
        flexShrink={0}
        description={
          <>
            {counts.map(({ reason, count }) => (
              <Text key={reason}>{t(`${KEY}.count.${reason}`, { count })}</Text>
            ))}
          </>
        }
        status="error"
        title={
          refused.acceptedCount > 0
            ? t(`${KEY}.title`, { count: refused.acceptedCount })
            : undefined
        }
      >
        <AlertActions>
          <TertiaryButton onClick={() => setDetailsOpen(true)} size="sm">
            {t(`${KEY}.detailsButton`)}
          </TertiaryButton>
          <AlertDismissButton onClick={onDismiss} />
        </AlertActions>
      </Alert>
      <Modal
        footer={
          <TertiaryButton onClick={() => setDetailsOpen(false)} size="md">
            {t('common:action.close')}
          </TertiaryButton>
        }
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title={t(`${KEY}.detailsTitle`)}
      >
        <Box as="ul" fontVariantNumeric="tabular-nums" listStyleType="none">
          {refused.bids.map(({ bid, reason }) => (
            <Box key={bid.id} as="li" paddingY={1}>
              <Text>
                {formatWithdrawSummary(t, {
                  timeCode: bid.timeCode,
                  areaCode: bid.areaCode,
                  direction: bid.direction,
                  orderMethod: bid.orderMethod,
                  priceYenPerKwh: bid.priceYenPerKwh,
                  // 行の volumeMw は MW 保持 (AC-BIN-043)。 概要も MW で出すので換算しない。
                  volumeMw: bid.volumeMw,
                })}
              </Text>
              <Text textStyle="captionNormal" color="Text.midEmphasis">
                {t(`${KEY}.reason.${reason}`)}
              </Text>
            </Box>
          ))}
        </Box>
      </Modal>
    </>
  )
}
